import { useState, useEffect } from 'react';
import { UserPlus, Shield, User, Info, Eye, EyeOff, Ban, Trash2, Key, Settings, RotateCcw, Search, ShieldCheck, ShieldOff, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

// AUTHORITATIVE BOH ROLE LIST - All roles must be visible and selectable
const BOH_ROLES = [
  { 
    value: 'super_admin', 
    label: 'Super Admin', 
    description: 'Full system control - all orders, prep, payments, costs, reports, audit logs, dashboards, user management, settings',
    color: 'bg-red-100 text-red-800 border-red-200',
    canSee: 'Everything',
    cannotSee: 'N/A'
  },
  { 
    value: 'manager', 
    label: 'Manager', 
    description: 'All access EXCEPT system settings - orders, prep, payments, costs, reports, user management',
    color: 'bg-pink-100 text-pink-800 border-pink-200',
    canSee: 'Orders, Prep, Payments, Costs, Reports, Users, Invitations',
    cannotSee: 'System Settings'
  },
  { 
    value: 'operations_manager', 
    label: 'Operations Manager', 
    description: 'Operational control - orders, order status, prep sheets, kitchen dashboards, stock levels',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    canSee: 'Orders, Prep Sheets, Stock, Kitchen Dashboard',
    cannotSee: 'Costs, Paystack Fees, Financial Totals, Audit Logs'
  },
  { 
    value: 'kitchen', 
    label: 'Kitchen Staff', 
    description: 'Production only - daily prep sheets, meal images, quantities per meal',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    canSee: 'Daily Prep Sheets, Meal Images, Quantities',
    cannotSee: 'Orders, Customer Data, Prices, Payments, Costs, Sales Dashboards'
  },
  { 
    value: 'customer_support', 
    label: 'Customer Support', 
    description: 'Customer interaction - messages, order numbers (reference only), food safety cases',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    canSee: 'Messages, Order References, Food Safety Cases, Reviews',
    cannotSee: 'Order Values, Prep Sheets, Payments, Costs, Reports'
  },
  { 
    value: 'finance', 
    label: 'Finance', 
    description: 'Full financial visibility - orders (read-only), payments, costs, sales dashboards, invoices, audit logs',
    color: 'bg-green-100 text-green-800 border-green-200',
    canSee: 'Orders (Read), Paystack Fees, Sales, Invoices, Audit Logs, Reconciliation',
    cannotSee: 'Edit Menus, Publish Menus, Verify Collection, Kitchen Prep'
  },
  { 
    value: 'driver', 
    label: 'Driver', 
    description: 'Delivery driver - view orders, verify collection codes, see delivery addresses',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    canSee: 'Orders, Verify Collection, Delivery Addresses, Google Maps',
    cannotSee: 'Edit Menus, Settings, Reports, Financial Data'
  },
];

// Available permissions for override
const AVAILABLE_PERMISSIONS = [
  { value: 'orders', label: 'View Orders' },
  { value: 'orders.edit', label: 'Edit Orders' },
  { value: 'orders.verify', label: 'Verify Collection' },
  { value: 'menu', label: 'View Menu' },
  { value: 'menu.edit', label: 'Edit Menu' },
  { value: 'menu.schedule', label: 'Schedule Menu' },
  { value: 'kitchen', label: 'View Kitchen' },
  { value: 'kitchen.prep', label: 'Manage Prep' },
  { value: 'stock', label: 'View Stock' },
  { value: 'stock.edit', label: 'Edit Stock' },
  { value: 'messages', label: 'Messages' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'food_safety', label: 'Food Safety' },
  { value: 'reports', label: 'View Reports' },
  { value: 'reports.export', label: 'Export Reports' },
  { value: 'users', label: 'View Users' },
  { value: 'users.edit', label: 'Edit Users' },
  { value: 'users.invite', label: 'Invite Users' },
  { value: 'settings', label: 'System Settings' },
];

// Customer role for filter only
const CUSTOMER_ROLE = {
  value: 'customer',
  label: 'Customer',
  description: 'End user - ordering only',
  color: 'bg-gray-100 text-gray-800 border-gray-200'
};

const getRoleBadgeColor = (role) => {
  const roleConfig = BOH_ROLES.find(r => r.value === role) || (role === 'customer' ? CUSTOMER_ROLE : null);
  return roleConfig?.color || 'bg-gray-100 text-gray-800 border-gray-200';
};

const getRoleLabel = (roleValue) => {
  const role = BOH_ROLES.find(r => r.value === roleValue) || (roleValue === 'customer' ? CUSTOMER_ROLE : null);
  return role?.label || roleValue;
};

export default function AdminUsers() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'boh', 'customer'
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [grantBohDialogOpen, setGrantBohDialogOpen] = useState(false);
  const [grantBohUser, setGrantBohUser] = useState(null);
  const [grantBohRole, setGrantBohRole] = useState('');
  const [grantingBoh, setGrantingBoh] = useState(false);
  const [selectedUserForPerms, setSelectedUserForPerms] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out deleted users
        setUsers(data.filter(u => !u.is_deleted));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!formData.role) {
      toast.error('Please select a role for this user');
      return;
    }
    
    if (!formData.password || formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create user');
      }

      toast.success('User created successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) throw new Error('Failed to update role');

      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!confirm(`Are you sure you want to delete ${userEmail}? This action cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }

      toast.success(`User ${userEmail} has been deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const cleanupTestUsers = async () => {
    if (!confirm('This will permanently delete all test users (emails containing "test", "@example.", etc.). This cannot be undone. Continue?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/cleanup-test-users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cleanup test users');
      }

      const data = await response.json();
      toast.success(`Deleted ${data.deleted_count} test users`);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const permanentlyDeleteUser = async (userId, userEmail) => {
    if (!confirm(`PERMANENT DELETE: Are you sure you want to permanently delete ${userEmail}? All associated data will be removed. This CANNOT be undone.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to permanently delete user');
      }

      toast.success(`User ${userEmail} has been permanently deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetUserPassword = async (userId, userName, userEmail) => {
    if (!confirm(`Send password reset link to ${userName} (${userEmail})? They will receive an email to set a new password.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reset password');
      }

      const data = await response.json();
      
      if (data.email_sent) {
        toast.success(`Password reset link sent to ${userEmail}`);
      } else if (data.reset_url) {
        // Copy reset link to clipboard if email failed
        navigator.clipboard.writeText(data.reset_url).then(() => {
          toast.info('Email failed — reset link copied to clipboard. Share it with the user manually.');
        }).catch(() => {
          toast.info(`Email failed. Share this link manually: ${data.reset_url}`);
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const grantBohAccess = async () => {
    if (!grantBohUser || !grantBohRole) return;
    setGrantingBoh(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/users/${grantBohUser.user_id}/role`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: grantBohRole })
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Failed'); }
      toast.success(`${grantBohUser.name} granted ${grantBohRole.replace(/_/g, ' ')} access`);
      setGrantBohDialogOpen(false);
      setGrantBohUser(null);
      setGrantBohRole('');
      fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setGrantingBoh(false); }
  };

  const revokeBohAccess = async (userId, userName) => {
    if (!confirm(`Revoke BOH access for ${userName}? They will become a Customer.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: 'customer' })
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Failed'); }
      toast.success(`${userName} BOH access revoked`);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const openPermissionsDialog = async (targetUser) => {
    setSelectedUserForPerms(targetUser);
    
    // Fetch current permissions
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${targetUser.user_id}/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.permission_overrides || []);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions([]);
    }
    
    setPermissionsDialogOpen(true);
  };

  const togglePermission = (permission) => {
    setUserPermissions(prev => 
      prev.includes(permission) 
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const savePermissions = async () => {
    if (!selectedUserForPerms) return;
    
    try {
      // Get current permissions to determine what to add/remove
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUserForPerms.user_id}/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      let currentPerms = [];
      if (response.ok) {
        const data = await response.json();
        currentPerms = data.permission_overrides || [];
      }
      
      // Calculate what to add and remove
      const toAdd = userPermissions.filter(p => !currentPerms.includes(p));
      const toRemove = currentPerms.filter(p => !userPermissions.includes(p));
      
      const updateResponse = await fetch(`${API_URL}/api/admin/users/${selectedUserForPerms.user_id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ add: toAdd, remove: toRemove })
      });
      
      if (!updateResponse.ok) {
        const error = await updateResponse.json();
        throw new Error(error.detail || 'Failed to update permissions');
      }
      
      toast.success('Permissions updated successfully');
      setPermissionsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deactivateUser = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/deactivate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to deactivate user');

      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Only super_admin can access this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Ban className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Denied</h2>
        <p className="text-slate-500">Only Super Admins can manage users.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-users">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-3">
            <Shield className="h-8 w-8 text-amber-500" />
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage BOH staff accounts and their roles</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={cleanupTestUsers}
            data-testid="cleanup-test-users-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Cleanup Test Users
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-600" data-testid="add-user-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Add BOH User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-amber-500" />
                  Add New BOH Staff User
                </DialogTitle>
              </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="John Doe"
                  data-testid="user-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  placeholder="john@gc3h.co.za"
                  data-testid="user-email"
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                    placeholder="Minimum 8 characters"
                    minLength={8}
                    data-testid="user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="role" className="flex items-center gap-2">
                  Role * 
                  <span className="text-xs text-red-500">(Required - determines access)</span>
                </Label>
                <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                  <SelectTrigger data-testid="user-role" className="h-auto min-h-[44px]">
                    <SelectValue placeholder="Select a role..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {BOH_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`${role.color} text-xs`}>{role.label}</Badge>
                          </div>
                          <span className="text-xs text-slate-500 whitespace-normal">
                            {role.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Role Permissions Info Box */}
              {formData.role && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-slate-500 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-medium text-slate-700 mb-1">
                        {getRoleLabel(formData.role)} Access:
                      </p>
                      <p className="text-green-600">
                        <strong>Can see:</strong> {BOH_ROLES.find(r => r.value === formData.role)?.canSee}
                      </p>
                      <p className="text-red-600 mt-1">
                        <strong>Cannot see:</strong> {BOH_ROLES.find(r => r.value === formData.role)?.cannotSee}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || !formData.role} 
                  className="bg-primary hover:bg-primary-600" 
                  data-testid="create-user-btn"
                >
                  {saving ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <Card className="mb-6 bg-slate-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-600" />
            Role Permissions Matrix
          </CardTitle>
          <CardDescription>Reference guide for BOH role capabilities</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-medium">Role</th>
                  <th className="text-center py-2 px-2 font-medium">Orders</th>
                  <th className="text-center py-2 px-2 font-medium">Prep</th>
                  <th className="text-center py-2 px-2 font-medium">Payments</th>
                  <th className="text-center py-2 px-2 font-medium">Costs</th>
                  <th className="text-center py-2 px-2 font-medium">Audit</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 px-2"><Badge className="bg-red-100 text-red-800 text-[10px]">Super Admin</Badge></td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-2"><Badge className="bg-pink-100 text-pink-800 text-[10px]">Manager</Badge></td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-2"><Badge className="bg-purple-100 text-purple-800 text-[10px]">Operations</Badge></td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-2"><Badge className="bg-orange-100 text-orange-800 text-[10px]">Kitchen</Badge></td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-2"><Badge className="bg-blue-100 text-blue-800 text-[10px]">Support</Badge></td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                </tr>
                <tr>
                  <td className="py-2 px-2"><Badge className="bg-green-100 text-green-800 text-[10px]">Finance</Badge></td>
                  <td className="text-center py-2">📖</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                </tr>
              </tbody>
            </table>
            <p className="text-[10px] text-slate-500 mt-2">✅ Full access | 📖 Read-only | ❌ No access</p>
          </div>
        </CardContent>
      </Card>

      {/* Category Tabs + Search + Role Filter */}
      <div className="mb-6 space-y-3">
        {/* Category tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl">
          {[
            { value: 'all', label: 'All Users', count: users.length },
            { value: 'boh', label: 'BOH Staff', count: users.filter(u => u.role !== 'customer').length },
            { value: 'customer', label: 'Customers', count: users.filter(u => u.role === 'customer').length },
          ].map(tab => (
            <button key={tab.value} onClick={() => setCategoryFilter(tab.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                categoryFilter === tab.value ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`} data-testid={`tab-${tab.value}`}>
              {tab.label} <span className="ml-1 text-xs opacity-60">({tab.count})</span>
            </button>
          ))}
        </div>
        {/* Search + role filter row */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, email, phone..." className="pl-10" data-testid="user-search" />
          </div>
          {categoryFilter === 'boh' && (
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40" data-testid="role-filter"><SelectValue placeholder="All roles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BOH Roles</SelectItem>
                {BOH_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {categoryFilter === 'customer' && (
            <Button onClick={() => setGrantBohDialogOpen(true)} variant="outline" className="flex-shrink-0" data-testid="grant-boh-btn">
              <ShieldCheck className="h-4 w-4 mr-2" /> Grant BOH Access
            </Button>
          )}
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (() => {
        // Filter users
        let filtered = users;
        if (categoryFilter === 'boh') filtered = filtered.filter(u => u.role !== 'customer');
        if (categoryFilter === 'customer') filtered = filtered.filter(u => u.role === 'customer');
        if (roleFilter !== 'all' && categoryFilter === 'boh') filtered = filtered.filter(u => u.role === roleFilter);
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          filtered = filtered.filter(u => (u.name||'').toLowerCase().includes(q) || (u.email||'').toLowerCase().includes(q) || (u.phone||'').toLowerCase().includes(q));
        }
        
        return filtered.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">User</th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">Role</th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 hidden md:table-cell">Joined</th>
                      <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map(u => (
                      <tr key={u.user_id} className="hover:bg-muted/30 transition-colors" data-testid={`user-${u.user_id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                              {u.picture ? <img src={u.picture} alt="" className="h-9 w-9 rounded-full" /> : <User className="h-4 w-4 text-slate-400" />}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-secondary-900 truncate">{u.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Badge className={`${getRoleBadgeColor(u.role)} text-[11px]`}>{getRoleLabel(u.role)}</Badge>
                            {u.role !== 'customer' && <span className="w-1.5 h-1.5 bg-green-500 rounded-full" title="BOH Access" />}
                          </div>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-xs text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Role change for BOH users */}
                            {u.role !== 'customer' && u.user_id !== user?.user_id && (
                              <Select value={u.role} onValueChange={(r) => updateUserRole(u.user_id, r)}>
                                <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{BOH_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                            {/* Grant BOH for customers */}
                            {u.role === 'customer' && (user?.role === 'super_admin' || user?.role === 'manager') && (
                              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setGrantBohUser(u); setGrantBohDialogOpen(true); }} title="Grant BOH Access">
                                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> BOH
                              </Button>
                            )}
                            {/* Revoke BOH */}
                            {u.role !== 'customer' && u.user_id !== user?.user_id && (user?.role === 'super_admin' || user?.role === 'manager') && (
                              <Button variant="ghost" size="sm" className="h-8 text-xs text-orange-600 hover:bg-orange-50" onClick={() => revokeBohAccess(u.user_id, u.name)} title="Revoke BOH Access">
                                <ShieldOff className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {/* Permissions */}
                            {u.role !== 'customer' && user?.role === 'super_admin' && u.user_id !== user?.user_id && (
                              <Button variant="ghost" size="sm" className="h-8" onClick={() => openPermissionsDialog(u)} title="Permissions"><Key className="h-3.5 w-3.5" /></Button>
                            )}
                            {/* Reset password */}
                            {(user?.role === 'super_admin' || user?.role === 'manager') && u.user_id !== user?.user_id && (
                              <Button variant="ghost" size="sm" className="h-8 text-amber-600 hover:bg-amber-50" onClick={() => resetUserPassword(u.user_id, u.name, u.email)} title="Reset Password">
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {/* Delete */}
                            {(user?.role === 'super_admin' || user?.role === 'manager') && u.user_id !== user?.user_id && (
                              <Button variant="ghost" size="sm" className="h-8 text-red-500 hover:bg-red-50" onClick={() => deleteUser(u.user_id, u.email)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchQuery ? 'No users match your search' : 'No users found'}</p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Grant BOH Access Dialog */}
      <Dialog open={grantBohDialogOpen} onOpenChange={setGrantBohDialogOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Grant BOH Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {grantBohUser ? (
              <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center"><User className="h-5 w-5 text-slate-400" /></div>
                <div><p className="font-medium text-sm">{grantBohUser.name}</p><p className="text-xs text-muted-foreground">{grantBohUser.email}</p></div>
              </div>
            ) : (
              <div>
                <Label className="text-sm">Select a customer to grant BOH access</Label>
                <p className="text-xs text-muted-foreground mt-1">Go to the Customers tab and click the "BOH" button on the user you want to grant access to.</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">BOH Role</Label>
              <Select value={grantBohRole} onValueChange={setGrantBohRole}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select a role..." /></SelectTrigger>
                <SelectContent>{BOH_ROLES.filter(r => r.value !== 'super_admin').map(r => (
                  <SelectItem key={r.value} value={r.value}>
                    <div><span className="font-medium">{r.label}</span><span className="text-xs text-muted-foreground ml-2">— {r.description?.slice(0, 60)}</span></div>
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGrantBohDialogOpen(false); setGrantBohUser(null); setGrantBohRole(''); }}>Cancel</Button>
            <Button onClick={grantBohAccess} disabled={!grantBohUser || !grantBohRole || grantingBoh} data-testid="confirm-grant-boh">
              {grantingBoh ? 'Granting...' : 'Grant Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-500" />
              Permission Overrides - {selectedUserForPerms?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Grant additional permissions beyond the default role permissions. 
              These overrides allow specific users to access features not normally available to their role.
            </p>
            
            <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
              {AVAILABLE_PERMISSIONS.map(perm => (
                <div 
                  key={perm.value}
                  className="flex items-center gap-2 p-2 rounded hover:bg-slate-50"
                >
                  <Checkbox
                    id={perm.value}
                    checked={userPermissions.includes(perm.value)}
                    onCheckedChange={() => togglePermission(perm.value)}
                  />
                  <Label htmlFor={perm.value} className="text-sm cursor-pointer">
                    {perm.label}
                  </Label>
                </div>
              ))}
            </div>
            
            {userPermissions.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-800">Active Overrides:</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {userPermissions.map(p => (
                    <Badge key={p} variant="outline" className="text-xs bg-white">
                      {AVAILABLE_PERMISSIONS.find(ap => ap.value === p)?.label || p}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={savePermissions}>
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
